import { useState, useEffect, useCallback, useMemo } from 'react';
import { api, Spinner, fmtDateTime } from './SecretaryPortal';
import { useEmailComposer } from './EmailComposer';

// ─── Email entrants ───────────────────────────────────────────────────────────
//
// The event secretary emails everyone with an online entry in the groups
// they tick (e.g. final instructions to every accepted entrant). The server
// sends one email to each person (never a shared list, so nobody sees anyone
// else's address), one per person even if they sent two entries, with
// Reply-To the event secretary. Sending runs in the background; this polls
// for progress. A request key made once per message stops a double click
// sending it twice. See /api/secretary/entries/:id/email-entrants.

const GROUPS = [
  { status: 'accepted', label: 'Accepted' },
  { status: 'pending',  label: 'Held for review' },
  { status: 'received', label: 'Received, not yet reviewed' },
];
const GROUP_LABEL = Object.fromEntries(GROUPS.map(g => [g.status, g.label]));

const newRequestKey = () =>
  (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);

// One address per person, ignoring capitals (matches the server).
const uniqueAddresses = (recipients, statuses) => {
  const seen = new Map();
  for (const r of recipients) {
    if (statuses.includes(r.status) && !seen.has(r.email.toLowerCase())) seen.set(r.email.toLowerCase(), r);
  }
  return [...seen.values()];
};

function SendProgress({ send }) {
  const done = send.sent_count + send.failed_count;
  if (!send.finished_at) {
    return <span>Sending… {done} of {send.recipient_count}</span>;
  }
  return (
    <span>
      Sent to {send.sent_count} of {send.recipient_count}
      {send.failed_count > 0 && (
        <span style={{ color: '#c62828' }}> ({send.failed_count} could not be sent)</span>
      )}
    </span>
  );
}

export default function EmailEntrantsDialog({ event, sessionToken, onClose }) {
  const [data, setData]           = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [statuses, setStatuses]   = useState(['accepted']);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending]     = useState(false);
  const [error, setError]         = useState(null);
  const [current, setCurrent]     = useState(null); // the send just started, while it runs
  const [requestKey, setRequestKey] = useState(newRequestKey);

  const { emailSubject, emailBody, emailPayload, renderComposer } = useEmailComposer(sessionToken, '');

  const path = `/api/secretary/entries/${event.CalendarID}/email-entrants`;
  const load = useCallback(async () => {
    try {
      const d = await api(path, {}, sessionToken);
      setData(d);
      setLoadError(null);
      return d;
    } catch (err) {
      setLoadError(err.message);
      return null;
    }
  }, [path, sessionToken]);

  useEffect(() => {
    let live = true;
    api(path, {}, sessionToken)
      .then(d => { if (live) setData(d); })
      .catch(err => { if (live) setLoadError(err.message); });
    return () => { live = false; };
  }, [path, sessionToken]);

  // While a send runs, refresh every 2 seconds to show its progress.
  useEffect(() => {
    if (!current || current.finished_at) return undefined;
    const t = setInterval(async () => {
      const d = await load();
      const latest = d?.history.find(h => h.id === current.id);
      if (latest) setCurrent(latest);
    }, 2000);
    return () => clearInterval(t);
  }, [current, load]);

  const recipients = useMemo(() => data?.recipients || [], [data]);
  const chosen = useMemo(() => uniqueAddresses(recipients, statuses), [recipients, statuses]);
  const countFor = (status) => uniqueAddresses(recipients, [status]).length;
  const toggle = (status) =>
    setStatuses(s => (s.includes(status) ? s.filter(x => x !== status) : [...s, status]));

  const canSend = chosen.length > 0 && emailSubject.trim() && emailBody.trim() && !sending
    && !(current && !current.finished_at);

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      const r = await api(path, {
        method: 'POST',
        body: JSON.stringify({ statuses, email: emailPayload(), request_key: requestKey }),
      }, sessionToken);
      setCurrent(r);
      setRequestKey(newRequestKey()); // a fresh key for any further message
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
      setConfirming(false);
    }
  };

  return (
    <div className="sec-overlay">
      <div className="sec-modal" style={{ maxWidth: '680px', maxHeight: '85vh', overflowY: 'auto' }}>
        <div className="sec-modal-header">
          <h3>✉️ Email entrants — {event.eventname || event.Hill}</h3>
          <button className="sec-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="sec-modal-body">
          {loadError && <div className="error-message"><p>⚠️ {loadError}</p></div>}
          {!data && !loadError && <Spinner />}
          {data && (
            <>
              <p className="muted-note" style={{ marginTop: 0 }}>
                Each person gets their own copy, so nobody sees anyone else&apos;s email address.
                Replies come to the event secretary. Entries typed in by hand (not sent online) have no
                email address here, so they aren&apos;t included.
              </p>

              <p className="form-section-label">Who to email</p>
              {GROUPS.map(g => (
                <label key={g.status} className="inline-checkbox" style={{ display: 'flex', marginBottom: '0.3rem' }}>
                  <input type="checkbox" checked={statuses.includes(g.status)} onChange={() => toggle(g.status)} />
                  {g.label} ({countFor(g.status)})
                </label>
              ))}
              <p style={{ margin: '0.5rem 0 1rem', fontWeight: 600 }}>
                Will email {chosen.length} {chosen.length === 1 ? 'person' : 'people'}
                {chosen.length > 0 && (
                  <details style={{ fontWeight: 400, fontSize: '0.85rem', marginTop: '0.25rem' }}>
                    <summary style={{ cursor: 'pointer' }}>Show who</summary>
                    <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.2rem' }}>
                      {chosen.map(r => (
                        <li key={r.email.toLowerCase()}>
                          {r.rider_name || '—'} &lt;{r.email}&gt; <span style={{ color: '#888' }}>{GROUP_LABEL[r.status]}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </p>

              <p className="form-section-label">Email</p>
              {renderComposer()}

              {error && (
                <div className="error-message" style={{ marginTop: '0.5rem' }}>
                  <p>⚠️ {error}</p>
                  <button onClick={() => setError(null)}>Dismiss</button>
                </div>
              )}

              {current && (
                <div className="info-banner" style={{ marginBottom: '1rem' }}>
                  <strong>{current.subject}:</strong> <SendProgress send={current} />
                  {current.duplicate && ' (this message had already been sent)'}
                </div>
              )}

              {data.history.length > 0 && (
                <>
                  <p className="form-section-label">Sent before</p>
                  <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.85rem' }}>
                    {data.history.map(h => (
                      <li key={h.id} style={{ marginBottom: '0.3rem' }}>
                        {fmtDateTime(h.created_at)}: <strong>{h.subject}</strong>
                        {h.attachment_filename && <> 📎 {h.attachment_filename}</>}
                        {' '}to {h.statuses.map(st => GROUP_LABEL[st] || st).join(', ')}. <SendProgress send={h} />
                        <span style={{ color: '#888' }}> ({h.sent_by})</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </div>

        <div className="sec-modal-footer">
          <button className="btn-cancel" onClick={onClose}>Close</button>
          <button className="btn-save" disabled={!canSend} onClick={() => setConfirming(true)}>
            Send to {chosen.length} {chosen.length === 1 ? 'person' : 'people'}
          </button>
        </div>
      </div>

      {confirming && (
        <div className="confirm-overlay">
          <div className="confirm-card">
            <h3>Send this email?</h3>
            <p>
              &ldquo;{emailSubject}&rdquo; will go to {chosen.length} {chosen.length === 1 ? 'person' : 'people'} straight
              away, each separately. It can&apos;t be called back once sent.
            </p>
            <div className="confirm-actions">
              <button className="btn-cancel" onClick={() => setConfirming(false)} disabled={sending}>Cancel</button>
              <button className="btn-confirm-delete" onClick={send} disabled={sending}>
                {sending ? 'Starting…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
